import PropTypes from "prop-types"
import React from "react"

import { StyledButton, StyledLink } from "./styled"

/**
 * This component renders a button or link for user interaction
 *
 * @param {object} props
 * @param {React.ReactNode} props.children - Text to display inside the button
 * @param {Function} props.handleClick - Function to run when the button is clicked
 * @param {string} props.href - URL to link to
 * @param {boolean} props.disabled - If true, the button will be disabled
 * @param {boolean} props.targetBlank - If true, the link will open in a new tab
 * @param {boolean} props.seo - If true, adds rel="noopener noreferrer" to the link
 * @param {boolean} props.primary - If true, the button will be styled as primary
 * @param {boolean} props.secondary - If true, the button will be styled as secondary
 * @param {boolean} props.ghost - If true, the button will be styled as ghost
 * @param {boolean} props.fullwidth - If true, the button will be full width
 * @param {boolean} props.fitContent - If true, the button width will fit its content
 * @param {boolean} props.rounded - If true, the button will be rounded
 * @param {boolean} props.small - If true, the button will be small
 * @param {boolean} props.jumbo - If true, the button will be jumbo
 * @param {boolean} props.submit - If true, the button will be a submit button
 * @param {boolean} props.reset - If true, the button will be a reset button
 * @returns {JSX.Element}
 */
const Button = React.forwardRef(
  (
    {
      children,
      handleClick,
      href,
      disabled = false,
      targetBlank = false,
      seo = false,
      primary = false,
      secondary = false,
      ghost = false,
      fullwidth = false,
      fitContent = false,
      rounded = false,
      small = false,
      jumbo = false,
      submit,
      reset,
      ...props
    },
    ref,
  ) => {
    const variant = (primary && "primary") || (secondary && "secondary") || (ghost && "ghost") || "standard"

    const styleProps = {
      $variant: variant,
      $width: fullwidth ? "100%" : fitContent ? "fit-content" : "auto",
      $rounded: rounded,
      $small: small,
      $jumbo: jumbo,
      $disabled: disabled,
    }

    if (href) {
      return (
        <StyledLink
          ref={ref}
          href={href}
          {...(targetBlank ? { target: "_blank" } : {})}
          {...(seo ? { rel: "noopener noreferrer" } : {})}
          {...styleProps}
          {...props}
        >
          {children}
        </StyledLink>
      )
    }

    const type = (submit && "submit") || (reset && "reset") || "button"

    return (
      <StyledButton ref={ref} onClick={!disabled ? handleClick : undefined} type={type} {...styleProps} {...props}>
        {children}
      </StyledButton>
    )
  },
)

Button.displayName = "Button"

Button.propTypes = {
  /** The text to display inside the button */
  children: PropTypes.node,
  /** Function to run when the button is clicked */
  handleClick: PropTypes.func,
  /** URL to link to */
  href: PropTypes.string,
  /** If true, the button will be disabled */
  disabled: PropTypes.bool,
  /** If true, the link will open in a new tab */
  targetBlank: PropTypes.bool,
  /** If true, adds rel="noopener noreferrer" to the link */
  seo: PropTypes.bool,
  /** If true, the button will be styled as primary */
  primary: PropTypes.bool,
  /** If true, the button will be styled as secondary */
  secondary: PropTypes.bool,
  /** If true, the button will be styled as ghost */
  ghost: PropTypes.bool,
  /** If true, the button will be full width */
  fullwidth: PropTypes.bool,
  /** If true, the button width will fit its content */
  fitContent: PropTypes.bool,
  /** If true, the button will be rounded */
  rounded: PropTypes.bool,
  /** If true, the button will be small */
  small: PropTypes.bool,
  /** If true, the button will be jumbo */
  jumbo: PropTypes.bool,
  /** If true, the button will be a submit button */
  submit: PropTypes.bool,
  /** If true, the button will be a reset button */
  reset: PropTypes.bool,
}

export { Button }
